import React, { useState } from "react";
import { Card, Tabs, Typography, Space, Row, Col, Button, Tag } from "antd";

const { Title, Paragraph, Text } = Typography;

const AgenticAI = () => {
  const [agentActive, setAgentActive] = useState(false);
  const [currentAction, setCurrentAction] = useState(0);
  const [completedActions, setCompletedActions] = useState([]);

  const actions = [
    {
      id: 1,
      agent: "Understanding your request...",
      system: "User Input",
      systemIcon: "👤",
      detail: '"Book me a flight to Tokyo next week"',
    },
    {
      id: 2,
      agent: "Searching for flights...",
      system: "Flight Search API",
      systemIcon: "✈️",
      detail: "Found 12 flights to Tokyo",
    },
    {
      id: 3,
      agent: "Comparing prices...",
      system: "Price Comparison",
      systemIcon: "💰",
      detail: "Best price: $650 on United",
    },
    {
      id: 4,
      agent: "Checking your calendar...",
      system: "Calendar API",
      systemIcon: "📅",
      detail: "You're free Tuesday-Friday",
    },
    {
      id: 5,
      agent: "Checking seat availability...",
      system: "Airline Database",
      systemIcon: "💺",
      detail: "Window seats available",
    },
    {
      id: 6,
      agent: "Processing payment...",
      system: "Payment Gateway",
      systemIcon: "💳",
      detail: "Payment successful",
    },
    {
      id: 7,
      agent: "Booking confirmed!",
      system: "Booking System",
      systemIcon: "🏢",
      detail: "Ticket sent to your email",
    },
  ];

  const tools = [
    {
      name: "LangChain",
      description:
        "Framework for building AI agents that connect to data, APIs, and external tools.",
      features: ["Multi-step reasoning", "Integrations", "Open-source"],
      url: "https://www.langchain.com",
    },
    {
      name: "AutoGPT",
      description:
        "Autonomous agent that sets goals, breaks them into tasks, and executes iteratively.",
      features: ["Goal-driven", "Self-executing", "Open-source"],
      url: "https://autogpt.net",
    },
    {
      name: "CrewAI",
      description:
        "Platform for multi-agent collaboration, letting specialized agents work together.",
      features: ["Teamwork", "Workflow automation", "Coordination"],
      url: "https://www.crewai.com",
    },
    {
      name: "Lovable",
      description:
        "Chat-based platform to build full-stack apps with AI, no coding required.",
      features: ["App builder", "Frontend + Backend", "Rapid prototyping"],
      url: "https://lovable.dev",
    },
    {
      name: "Microsoft Copilot Studio",
      description:
        "Enterprise platform to design and deploy agentic AI experiences across Microsoft 365.",
      features: ["Business automation", "Integration", "Enterprise-ready"],
      url: "https://copilot.microsoft.com",
    },
    {
      name: "AWS Agents (Bedrock)",
      description:
        "Cloud-native agentic AI services with scalability and enterprise integration.",
      features: ["Cloud-native", "Secure", "Scalable"],
      url: "https://aws.amazon.com/bedrock",
    },
  ];

  const runAgent = () => {
    setAgentActive(true);
    setCurrentAction(0);
    setCompletedActions([]);

    let step = 0;
    const interval = setInterval(() => {
      if (step < actions.length) {
        setCurrentAction(step);
        if (step > 0) {
          setCompletedActions((prev) => [...prev, step - 1]);
        }
        step++;
      } else {
        setCompletedActions((prev) => [...prev, actions.length - 1]);
        clearInterval(interval);
        setAgentActive(false);
      }
    }, 1800);
  };

  const resetAgent = () => {
    setAgentActive(false);
    setCurrentAction(0);
    setCompletedActions([]);
  };

  const renderAgenticAI = () => (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card
        title={<span style={{ color: "#0071e3" }}>What is Agentic AI?</span>}
      >
        <Paragraph style={{ fontSize: "16px" }}>
          Imagine asking a really smart assistant to "book me a flight to Tokyo"
          and they actually do everything - search flights, compare prices,
          check your calendar, process payment - without you having to guide
          each step. That's Agentic AI!
        </Paragraph>
        <Paragraph>
          Unlike traditional AI that just answers questions,{" "}
          <Text strong>Agentic AI can:</Text>
        </Paragraph>
        <ul style={{ paddingLeft: "20px" }}>
          <li>Understand complex goals</li>
          <li>Break them into smaller tasks</li>
          <li>Execute actions autonomously</li>
          <li>Interact with external systems and tools</li>
          <li>Make decisions along the way</li>
          <li>Adapt when things go wrong</li>
        </ul>
      </Card>

      <Card
        title={<span style={{ color: "#0071e3" }}>Why Agentic AI Matters</span>}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <div
              style={{
                padding: "16px",
                border: "1px solid #0071e3",
                borderRadius: "8px",
                height: "100%",
              }}
            >
              <Text strong style={{ fontSize: "16px" }}>
                Benefits
              </Text>
              <ul
                style={{
                  paddingLeft: "20px",
                  marginTop: "8px",
                  marginBottom: 0,
                }}
              >
                <li>Saves massive amounts of time</li>
                <li>Handles complex multi-step tasks</li>
                <li>Works 24/7 without breaks</li>
                <li>Reduces human error</li>
                <li>Scales to handle many tasks simultaneously</li>
                <li>Learns and improves over time</li>
              </ul>
            </div>
          </Col>
          <Col xs={24} md={12}>
            <div
              style={{
                padding: "16px",
                border: "1px solid #0071e3",
                borderRadius: "8px",
                height: "100%",
              }}
            >
              <Text strong style={{ fontSize: "16px" }}>
                Important Considerations
              </Text>
              <ul
                style={{
                  paddingLeft: "20px",
                  marginTop: "8px",
                  marginBottom: 0,
                }}
              >
                <li>Needs clear goals and boundaries</li>
                <li>Requires human oversight for critical decisions</li>
                <li>Can make mistakes if poorly designed</li>
                <li>Privacy and security must be carefully managed</li>
                <li>Ethical guidelines are essential</li>
              </ul>
            </div>
          </Col>
        </Row>
      </Card>
    </Space>
  );

  const renderComparison = () => (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Card
            title={<span style={{ color: "#0071e3" }}>Traditional AI</span>}
            style={{ height: "100%" }}
          >
            <Paragraph>
              <Text strong>You:</Text> "What flights are available to Tokyo?"
              <br />
              <Text strong>AI:</Text> "Here are 12 flights to Tokyo."
              <br />
              <br />
              <br />
              <Text type="secondary">
                One question → One answer. You do the rest.
              </Text>
            </Paragraph>
            <div
              style={{
                marginTop: "16px",
                padding: "12px",
                border: "1px solid #0071e3",
                borderRadius: "8px",
              }}
            >
              <Text strong>Characteristics:</Text>
              <ul
                style={{
                  paddingLeft: "20px",
                  marginBottom: 0,
                  marginTop: "8px",
                }}
              >
                <li>Responds to single requests</li>
                <li>No planning or follow-up</li>
                <li>Can't take actions</li>
                <li>Waits for your next command</li>
              </ul>
            </div>
          </Card>
        </Col>

        <Col xs={24} md={12}>
          <Card
            title={<span style={{ color: "#0071e3" }}>Agentic AI</span>}
            style={{ height: "100%" }}
          >
            <Paragraph>
              <Text strong>You:</Text> "Book me a flight to Tokyo next week."
              <br />
              <Text strong>AI:</Text> Searches flights, compares prices, checks
              your calendar, books ticket, processes payment, sends confirmation
              to you
              <br />
              <br />
              <Text type="secondary">
                One goal → AI handles everything autonomously.
              </Text>
            </Paragraph>
            <div
              style={{
                marginTop: "16px",
                padding: "12px",
                border: "1px solid #0071e3",
                borderRadius: "8px",
              }}
            >
              <Text strong>Characteristics:</Text>
              <ul
                style={{
                  paddingLeft: "20px",
                  marginBottom: 0,
                  marginTop: "8px",
                }}
              >
                <li>Understands complex goals</li>
                <li>Plans multi-step actions</li>
                <li>Executes tasks independently</li>
                <li>Interacts with multiple systems</li>
              </ul>
            </div>
          </Card>
        </Col>
      </Row>
    </Space>
  );

  const renderDemo = () => (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card
        title={
          <span style={{ color: "#0071e3" }}>
            Watch an AI Agent Book a Flight
          </span>
        }
      >
        <Paragraph>
          See how an AI agent autonomously interacts with multiple systems to
          complete a complex task
        </Paragraph>

        <div
          style={{
            textAlign: "center",
            marginBottom: "60px",
            marginTop: "32px",
          }}
        >
          <Space>
            <Button
              type="primary"
              size="large"
              onClick={runAgent}
              disabled={agentActive}
            >
              {agentActive ? "Agent Working..." : "Start Booking"}
            </Button>
            <Button disabled={agentActive} size="large" onClick={resetAgent}>
              Reset
            </Button>
          </Space>
        </div>

        <div style={{ position: "relative", minHeight: "500px" }}>
          {/* AI Agent Robot - Center */}
          <div
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              zIndex: 10,
            }}
          >
            <div
              style={{
                width: "120px",
                height: "120px",
                background: agentActive ? "#0071e3" : "#d9d9d9",
                borderRadius: "20px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "48px",
                transition: "all 0.3s ease",
                boxShadow: agentActive
                  ? "0 8px 24px rgba(0, 113, 227, 0.3)"
                  : "none",
              }}
            >
              🤖
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "white",
                  marginTop: "4px",
                }}
              >
                AI Agent
              </div>
            </div>

            {/* Current Action Below Robot */}
            {agentActive && currentAction < actions.length && (
              <div
                style={{
                  marginTop: "1px",
                  padding: "8px 16px",
                  fontStyle: "italic",
                  color: "#0071e3",
                  fontSize: "14px",
                  textAlign: "center",
                  whiteSpace: "nowrap",
                }}
              >
                {actions[currentAction].agent}
              </div>
            )}

            {/* Final message after completion */}
            {!agentActive && completedActions.length === actions.length && (
              <div
                style={{
                  marginTop: "16px",
                  padding: "8px 16px",
                  fontStyle: "italic",
                  color: "#52c41a",
                  fontSize: "14px",
                  textAlign: "center",
                  fontWeight: 600,
                }}
              >
                ✅ Your ticket is booked!
              </div>
            )}
          </div>

          {/* External Systems arranged in a circle */}
          {actions.map((action, index) => {
            const angle = (index * 360) / actions.length - 90;
            const radius = 220;
            const x = Math.cos((angle * Math.PI) / 180) * radius;
            const y = Math.sin((angle * Math.PI) / 180) * radius;

            const isActive = index === currentAction && agentActive;
            const isCompleted = completedActions.includes(index);

            return (
              <div key={action.id}>
                {/* Connection Line */}
                {isActive && (
                  <svg
                    style={{
                      position: "absolute",
                      left: 0,
                      top: 0,
                      width: "100%",
                      height: "100%",
                      pointerEvents: "none",
                      zIndex: 5,
                    }}
                  >
                    <line
                      x1="50%"
                      y1="50%"
                      x2={`calc(50% + ${x}px)`}
                      y2={`calc(50% + ${y}px)`}
                      stroke="#0071e3"
                      strokeWidth="3"
                      strokeDasharray="5,5"
                      style={{ animation: "dash 1s linear infinite" }}
                    />
                  </svg>
                )}

                {/* Final connection back to User when complete */}
                {!agentActive &&
                  completedActions.length === actions.length &&
                  index === 0 && (
                    <svg
                      style={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        width: "100%",
                        height: "100%",
                        pointerEvents: "none",
                        zIndex: 5,
                      }}
                    >
                      <line
                        x1="50%"
                        y1="50%"
                        x2={`calc(50% + ${x}px)`}
                        y2={`calc(50% + ${y}px)`}
                        stroke="#52c41a"
                        strokeWidth="3"
                        strokeDasharray="5,5"
                        style={{ animation: "dash 1s linear infinite" }}
                      />
                    </svg>
                  )}

                {/* External System Block */}
                <div
                  style={{
                    position: "absolute",
                    left: `calc(50% + ${x}px)`,
                    top: `calc(50% + ${y}px)`,
                    transform: "translate(-50%, -50%)",
                    width: "150px",
                    padding: "12px",
                    background: isCompleted
                      ? "#f6ffed"
                      : isActive
                      ? "#e6f7ff"
                      : "#fafafa",
                    border: `2px solid ${
                      isCompleted ? "#52c41a" : isActive ? "#0071e3" : "#d9d9d9"
                    }`,
                    borderRadius: "12px",
                    textAlign: "center",
                    transition: "all 0.3s ease",
                    boxShadow: isActive
                      ? "0 4px 12px rgba(0, 113, 227, 0.2)"
                      : "none",
                  }}
                >
                  <div style={{ fontSize: "32px", marginBottom: "4px" }}>
                    {action.systemIcon}
                  </div>
                  <div
                    style={{
                      fontSize: "11px",
                      fontWeight: 600,
                      color: isCompleted
                        ? "#52c41a"
                        : isActive
                        ? "#0071e3"
                        : "#666",
                    }}
                  >
                    {action.system}
                  </div>
                  {isActive && (
                    <div
                      style={{
                        marginTop: "8px",
                        fontSize: "10px",
                        color: "#666",
                        fontStyle: "italic",
                      }}
                    >
                      {action.detail}
                    </div>
                  )}
                  {isCompleted && !isActive && (
                    <div style={{ marginTop: "4px", fontSize: "16px" }}>✓</div>
                  )}
                  {/* Show confirmation message on User block when complete */}
                  {!agentActive &&
                    completedActions.length === actions.length &&
                    index === 0 && (
                      <div
                        style={{
                          marginTop: "8px",
                          fontSize: "10px",
                          color: "#52c41a",
                          fontWeight: 600,
                          fontStyle: "italic",
                        }}
                      >
                        Ticket booked!
                      </div>
                    )}
                </div>
              </div>
            );
          })}
        </div>

        {completedActions.length === actions.length && (
          <Card
            style={{
              marginTop: "42px",
              borderColor: "#0071e3",
              borderWidth: "2px",
            }}
          >
            <div style={{ textAlign: "justify" }}>
              <Title level={4} style={{ color: "#0071e3" }}>
                Flight Booked Successfully!
              </Title>
              <Paragraph>
                The AI agent autonomously handled everything - searched flights,
                compared prices, checked your calendar, selected the best
                option, processed payment, and confirmed your booking. All from
                one simple request!
              </Paragraph>
            </div>
          </Card>
        )}

        <style>{`
          @keyframes dash {
            to {
              stroke-dashoffset: -10;
            }
          }
        `}</style>
      </Card>

      <Card
  title={<span style={{ color: "#0071e3" }}>Tools for Agentic AI</span>}
>
  <Space direction="vertical" size="middle" style={{ width: "100%" }}>
    {tools.map((tool, idx) => (
      <div
        key={idx}
        style={{
          padding: "16px",
          border: "1px solid #d9d9d9",
          borderRadius: "8px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "16px",
        }}
      >
        <div style={{ flex: 1 }}>
          <Title level={5} style={{ marginBottom: "8px" }}>
            {tool.name}
          </Title>
          <Paragraph style={{ marginBottom: "8px", fontSize: "14px" }}>
            {tool.description}
          </Paragraph>
          <Space wrap>
            {tool.features.map((feature, i) => (
              <Tag key={i} color="blue">
                {feature}
              </Tag>
            ))}
          </Space>
        </div>
        <Button
          type="primary"
          onClick={() =>
            window.open(tool.url, "_blank", "noopener,noreferrer")
          }
        >
          Visit
        </Button>
      </div>
    ))}
  </Space>
</Card>

    </Space>
  );

  const renderRealUses = () => (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card
        title={<span style={{ color: "#0071e3" }}>Email Assistant Agent</span>}
      >
        <Paragraph style={{ marginBottom: 0 }}>
          <Text strong>You say:</Text> Schedule meetings with the team next week
        </Paragraph>
        <Paragraph style={{ marginBottom: 0 }}>
          <Text strong>Agent:</Text> Reads your calendar, checks team
          availability, sends meeting invites, handles conflicts, sends
          confirmations - all automatically.
        </Paragraph>
      </Card>

      <Card title={<span style={{ color: "#0071e3" }}>Research Agent</span>}>
        <Paragraph style={{ marginBottom: 0 }}>
          <Text strong>You say:</Text> Research the best electric cars under
          $40k
        </Paragraph>
        <Paragraph style={{ marginBottom: 0 }}>
          <Text strong>Agent:</Text> Searches multiple websites, compares specs,
          reads reviews, checks prices, creates comparison table, and provides
          recommendations.
        </Paragraph>
      </Card>

      <Card title={<span style={{ color: "#0071e3" }}>Coding Agent</span>}>
        <Paragraph style={{ marginBottom: 0 }}>
          <Text strong>You say:</Text> Build me a todo app with user
          authentication
        </Paragraph>
        <Paragraph style={{ marginBottom: 0 }}>
          <Text strong>Agent:</Text> Plans the architecture, writes frontend
          code, creates backend API, sets up database, writes tests, and deploys
          the application.
        </Paragraph>
      </Card>

      <Card title={<span style={{ color: "#0071e3" }}>Healthcare Agent</span>}>
        <Paragraph style={{ marginBottom: 0 }}>
          <Text strong>You say:</Text> I have symptoms headache, cough, and cold
        </Paragraph>
        <Paragraph style={{ marginBottom: 0 }}>
          <Text strong>Agent:</Text> Plans the architecture, writes frontend
          code, creates backend API, sets up database, writes tests, and deploys
          the application.
        </Paragraph>
      </Card>
    </Space>
  );

  const renderFuture = () => (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card
        title={
          <span style={{ color: "#0071e3" }}>The Future of Agentic AI</span>
        }
      >
        <Paragraph>
          We're moving from AI that just answers questions to AI that gets
          things done on behalf of us. In the near future, you might have
          personal AI agents that:
        </Paragraph>
        <ul style={{ paddingLeft: "20px" }}>
          <li>Manage your entire schedule and priorities</li>
          <li>Handle all your online shopping and errands</li>
          <li>Do your taxes and manage finances</li>
          <li>Coordinate with other people's AI agents</li>
          <li>Learn your preferences and anticipate your needs</li>
          <li>Act as your personal assistant for work and life</li>
        </ul>
        <Paragraph style={{ marginBottom: 0 }}>
          <Text strong>The key difference:</Text> Instead of telling AI what to
          do at every step, you just tell it what you want to achieve - and it
          figures out how to make it happen.
        </Paragraph>
      </Card>
    </Space>
  );

  const tabItems = [
    {
      key: "agentic",
      label: "Agentic AI",
      children: renderAgenticAI(),
    },
    {
      key: "comparison",
      label: "Comparison",
      children: renderComparison(),
    },
    {
      key: "demo",
      label: "Interactive Demo",
      children: renderDemo(),
    },
    {
      key: "realuses",
      label: "Real Uses",
      children: renderRealUses(),
    },
    {
      key: "future",
      label: "Future",
      children: renderFuture(),
    },
  ];

  return (
    <div className="page-container">
      <header
        style={{ textAlign: "center", marginBottom: "32px" }}
        className="fade-in-down"
      >
        <Title level={1}>Agentic AI</Title>
        <Paragraph style={{ fontSize: "18px" }}>
          AI that can think, plan, and act on its own to achieve goals
        </Paragraph>
      </header>

      <Tabs defaultActiveKey="agentic" items={tabItems} size="large" />
    </div>
  );
};

export default AgenticAI;
